import { map } from "rxjs/operators";
import {
  LanguageEnum,
  LanguageKeycloakEnum,
  angularToKeycloakCode
} from "@shared/enums/language.enum";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "@env";

@Injectable({
  providedIn: "root"
})
export class LanguageService {
  private languages$: BehaviorSubject<LanguageEnum> = new BehaviorSubject(
    environment.defaultLocale as LanguageEnum
  );

  constructor(
    private translateService: TranslateService,
    private http: HttpClient
  ) {
    this.setInitState();
  }

  private setLanguage$ = (lang: LanguageEnum) => this.languages$.next(lang);

  /**
   * @param isAngularCode True if the code to be return is on angular format.
   * If False, the keycloak's format will be returned.
   * @returns A observable with the current code of the language
   */
  getLanguage$ = (
    isAngularCode = false
  ): Observable<LanguageKeycloakEnum | LanguageEnum> =>
    this.languages$.asObservable().pipe(
      map((lang) => {
        if (isAngularCode) return lang;
        return angularToKeycloakCode(lang);
      })
    );

  setInitState() {
    this.translateService.addLangs([
      LanguageEnum.PT_BR,
      LanguageEnum.ES,
      LanguageEnum.EN_US,
      LanguageEnum.DE_DE,
      LanguageEnum.ES_ES,
      LanguageEnum.EN_EN,
      LanguageEnum.PT_PT
    ]);
  }

  /**
   * Set the language to be used by the system.
   * @param lang The language code, e.g., "pt-BR" if the current language is Brazilian Portuguese.
   */
  setLang(lang: LanguageEnum) {
    this.translateService.use(lang).subscribe({
      complete: () => {
        this.updateLanguage(angularToKeycloakCode(lang));
        this.setLanguage$(lang);
      }
    });
  }

  /**
   * Call the API to update the language value in the keycloak user.
   * @param code The language code, e.g., "pt-BR" if the current language is Brazilian Portuguese.
   */
  updateLanguage(code: string) {
    this.http
      .put<any>(`${environment.apiUrl}keycloack-users/locale?code=${code}`, {})
      .subscribe();
  }

  onLangChange() {
    return this.translateService.onLangChange;
  }

  /**
   * @returns The current value of the language used, e.g., "pt-BR" if the current language is Brazilian Portuguese.
   */
  getCurrentLang() {
    return this.translateService.currentLang;
  }

  /**
   * @returns True if the current language is Brazilian Portuguese. False otherwise.
   */
  currentLangIsPtBr() {
    return this.getCurrentLang() === LanguageEnum.PT_BR;
  }

  /**
   * @returns True if the current language is Argentinian Spanish. False otherwise.
   */
  currentLangIsAr() {
    return this.getCurrentLang() === LanguageEnum.ES;
  }

  /**
   * @returns True if the current language is American English. False otherwise.
   */
  currentLangIsEnUS() {
    return this.getCurrentLang() === LanguageEnum.EN_US;
  }

  /**
   * @returns True if the current language is Portugal Portuguese. False otherwise.
   */
  currentLangIsPtPt() {
    return this.getCurrentLang() === LanguageEnum.PT_PT;
  }

  /**
   * @returns True if the current language is Spain Spanish. False otherwise.
   */
  currentLangIsEsEs() {
    return this.getCurrentLang() === LanguageEnum.ES;
  }

  /**
   * @returns True if the current language is United Kingdom English. False otherwise.
   */
  currentLangIsEnEn() {
    return this.getCurrentLang() === LanguageEnum.EN_EN;
  }

  /**
   * @returns True if the current language is German. False otherwise.
   */
  currentLangIsDeDe() {
    return this.getCurrentLang() === LanguageEnum.DE_DE;
  }

  /**
   * @returns The translationService of the lib ngx-translate (https://github.com/ngx-translate/core)
   */
  getTranslateService() {
    return this.translateService;
  }

  /**
   * T The instant function on translateService is synchronous,
   * so sometimes it can be executed even before the transalations exists.
   * The  purpose of this function is to wait the get function of the translateService to be executed.
   */
  async instant(key: string | string[], interpolateParams?: Object) {
    return await this.translateService.get(key, interpolateParams).toPromise();
  }
}
